import React, { useCallback, useEffect, useRef, useState } from 'react';
import API from '../config/api';

const HeadsetLoginQrModal = ({
  open,
  onClose,
  qrApiPath,
  title = 'Headset Login QR',
  accentClass = 'text-qure-secondary',
  closeHoverClass = 'hover:text-qure-danger',
}) => {
  const qrCodeRef = useRef(null);
  const [error, setError] = useState('');
  const [expiresInMinutes, setExpiresInMinutes] = useState(60);

  const loadQrCode = useCallback(async () => {
    if (!open || !qrCodeRef.current) return;

    try {
      setError('');
      const response = await API.get(qrApiPath);

      if (!response.data.success || !response.data.encrypted_string) {
        throw new Error(response.data.message || 'Failed to load QR code');
      }

      const minutes = Number(response.data.expires_in_minutes) || 60;
      setExpiresInMinutes(minutes);

      qrCodeRef.current.innerHTML = '';
      const img = document.createElement('img');
      img.src = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&ecc=H&data=${encodeURIComponent(response.data.encrypted_string)}`;
      img.alt = 'Headset login QR code';
      img.style.width = '100%';
      img.style.maxWidth = '256px';
      img.style.height = 'auto';
      qrCodeRef.current.appendChild(img);
    } catch (err) {
      console.error('Error generating QR code:', err);
      const serverMessage = err.response?.data?.message;
      setError(serverMessage || 'Unable to load headset login QR. Please try again.');
      if (qrCodeRef.current) {
        qrCodeRef.current.innerHTML = '';
      }
    }
  }, [open, qrApiPath]);

  useEffect(() => {
    if (!open) return undefined;

    loadQrCode();
    const refreshMs = Math.max(5, Math.min(expiresInMinutes - 2, 30)) * 60 * 1000;
    const intervalId = window.setInterval(loadQrCode, refreshMs);

    return () => window.clearInterval(intervalId);
  }, [open, loadQrCode, expiresInMinutes]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-shellModal flex items-center justify-center bg-black/50 p-4 backdrop-blur"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[450px] overflow-y-auto rounded-shellLg bg-white p-8 shadow-shellModal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className={`m-0 flex items-center gap-2 text-2xl font-semibold ${accentClass}`}>
            <i className="bi bi-qr-code" /> {title}
          </h3>
          <button
            className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-xl text-slate-500 transition hover:bg-slate-50 ${closeHoverClass}`}
            onClick={onClose}
            type="button"
            aria-label="Close QR modal"
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <p className="mb-4 text-sm leading-6 text-slate-600">
          Scan this code with your ARC headset to sign in. No org code, PIN, or password is required on the device.
        </p>

        <div className="text-center">
          <div className="mb-3 flex w-full items-center justify-center rounded-shellMd border-2 border-slate-100 bg-white p-6">
            <div ref={qrCodeRef} className="flex min-h-[256px] items-center justify-center" />
          </div>
          {error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : (
            <p className="text-xs text-slate-500">
              QR refreshes automatically. Valid for about {expiresInMinutes} minutes.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeadsetLoginQrModal;
